import { useOrgCurrency } from '../../../../../hooks/useOrgCurrency.js';
import { formatDate as fmtDate } from '../../../../../i18n/format.js';
import React from 'react';
import {
  Car, Calendar, Euro, FileText, Shield, AlertTriangle, CheckCircle, XCircle,
  MapPin, User, Building, FileSignature
} from 'lucide-react';
import { EXIT_REASONS } from './StepExitContext.jsx';
import { EXIT_DOCUMENT_TYPES } from './StepExitDocuments.jsx';



/**
 * Format date
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return fmtDate(dateStr, 'long');
};

/**
 * Summary Section Component
 */
function SummarySection({ title, icon: Icon, children, variant = 'default' }) {
  const variants = {
    default: 'bg-gray-50 border-gray-200',
    success: 'bg-green-50 border-green-200',
    warning: 'bg-amber-50 border-amber-200',
    error: 'bg-red-50 border-red-200',
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${variants[variant]}`}>
      <div className={`px-4 py-2 border-b flex items-center gap-2 ${variants[variant]}`}>
        <Icon className="w-5 h-5 text-gray-600" />
        <h4 className="font-medium text-gray-900">{title}</h4>
      </div>
      <div className="p-4 bg-white">{children}</div>
    </div>
  );
}

/**
 * Summary Row Component
 */
function SummaryRow({ label, value, valueClassName = '' }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-sm font-medium ${valueClassName}`}>{value}</span>
    </div>
  );
}

/**
 * Validation Item Component
 */
function ValidationItem({ label, isValid, message }) {
  return (
    <div className="flex items-center gap-2 py-1">
      {isValid ? (
        <CheckCircle className="w-4 h-4 text-green-500" />
      ) : (
        <XCircle className="w-4 h-4 text-red-500" />
      )}
      <span className={`text-sm ${isValid ? 'text-green-700' : 'text-red-700'}`}>
        {label}
      </span>
      {!isValid && message && (
        <span className="text-xs text-red-500 ml-auto">{message}</span>
      )}
    </div>
  );
}

/**
 * StepExitSummary - Final review step before completing vehicle exit
 */
function StepExitSummary({ formData, vehicle }) {
  // Devise de l'organisation du vehicule - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const formatCurrency = (value) => (!value && value !== 0 ? '-' : money(value, vehicle));
  const selectedReason = EXIT_REASONS.find(r => r.value === formData.reason);
  const pendingDocs = formData.pendingExitDocuments || [];
  const insuranceLinks = formData.insurance_links || [];

  // Calculate financial summary
  const getExitAmount = () => {
    if (formData.sale_price_eur) return parseFloat(formData.sale_price_eur);
    if (formData.indemnity_amount_eur) return parseFloat(formData.indemnity_amount_eur);
    if (formData.scrapyard_price_eur) return parseFloat(formData.scrapyard_price_eur);
    if (formData.other_amount_eur) return parseFloat(formData.other_amount_eur);
    return 0;
  };

  const getTotalPenalties = () => {
    let total = 0;
    if (formData.leasing_penalty_amount_eur) total += parseFloat(formData.leasing_penalty_amount_eur);
    if (formData.lease_excess_km_cost) total += parseFloat(formData.lease_excess_km_cost);
    if (formData.lease_damage_cost) total += parseFloat(formData.lease_damage_cost);
    if (formData.deductible_amount) total += parseFloat(formData.deductible_amount);
    return total;
  };

  const getTotalRefunds = () => {
    return insuranceLinks
      .filter(l => l.refund_received && l.refund_amount_eur)
      .reduce((sum, l) => sum + parseFloat(l.refund_amount_eur), 0);
  };

  const exitAmount = getExitAmount();
  const penalties = getTotalPenalties();
  const refunds = getTotalRefunds();
  const netResult = exitAmount - penalties + refunds;

  // Validation checks
  const validations = {
    reason: !!formData.reason,
    date: !!formData.exit_effective_date,
    cerfa: pendingDocs.some(d => d.documentType === 'CERFA_CESSION'),
    registration: pendingDocs.some(d => d.documentType === 'REGISTRATION_CANCELLED'),
    leaseDoc: formData.reason !== 'lease_end' || pendingDocs.some(d => d.documentType === 'LEASE_END_DOCUMENT'),
    insurance: insuranceLinks.length === 0 || insuranceLinks.every(l => l.termination_confirmed),
  };

  const allValid = Object.values(validations).every(v => v);

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Resume de la Sortie</h3>

      {/* Vehicle Info */}
      <SummarySection title="Vehicule" icon={Car}>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
            <Car className="w-8 h-8 text-gray-400" />
          </div>
          <div className="flex-1">
            <p className="text-lg font-semibold text-gray-900">
              {vehicle?.make} {vehicle?.model}
            </p>
            <p className="text-sm text-gray-600">{vehicle?.license_plate}</p>
            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
              <span>Annee: {vehicle?.year}</span>
              <span>VIN: {vehicle?.vin}</span>
            </div>
          </div>
        </div>
      </SummarySection>

      {/* Exit Context */}
      <SummarySection title="Contexte de Sortie" icon={FileSignature}>
        <div className="space-y-1">
          <SummaryRow
            label="Motif de sortie"
            value={selectedReason?.label || '-'}
            valueClassName="text-red-600 font-semibold"
          />
          {formData.reason === 'other' && formData.other_nature_text && (
            <SummaryRow label="Precision" value={formData.other_nature_text} />
          )}
          <SummaryRow label="Date effective" value={formatDate(formData.exit_effective_date)} />
          <SummaryRow
            label="Mode assurance"
            value={formData.insurance_mode === 'fleet' ? 'Contrat Flotte' : 'Contrat Individuel'}
          />
          <SummaryRow
            label="Relance assurance J+10"
            value={formData.exit_effective_date
              ? formatDate(new Date(new Date(formData.exit_effective_date).getTime() + 10 * 24 * 60 * 60 * 1000).toISOString())
              : '-'}
          />
        </div>
      </SummarySection>

      {/* Financial Summary */}
      <SummarySection title="Bilan Financier" icon={Euro}>
        <div className="space-y-1">
          <SummaryRow
            label={selectedReason?.value === 'sale' ? 'Prix de vente' :
                   selectedReason?.value === 'breakdown' ? 'Prix casse' :
                   selectedReason?.value === 'seizure' ? 'Perte comptable' :
                   'Montant'}
            value={formatCurrency(exitAmount)}
            valueClassName="text-green-600"
          />
          {penalties > 0 && (
            <SummaryRow
              label="Penalites / Couts"
              value={`- ${formatCurrency(penalties)}`}
              valueClassName="text-red-600"
            />
          )}
          {refunds > 0 && (
            <SummaryRow
              label="Remboursements assurance"
              value={`+ ${formatCurrency(refunds)}`}
              valueClassName="text-green-600"
            />
          )}
          <div className="border-t mt-2 pt-2">
            <SummaryRow
              label="Resultat net"
              value={formatCurrency(netResult)}
              valueClassName={`text-lg font-bold ${netResult >= 0 ? 'text-green-600' : 'text-red-600'}`}
            />
          </div>
        </div>
      </SummarySection>

      {/* Documents Summary */}
      <SummarySection title={`Documents (${pendingDocs.length})`} icon={FileText}>
        {pendingDocs.length === 0 ? (
          <p className="text-sm text-gray-500">Aucun document ajoute</p>
        ) : (
          <div className="space-y-2">
            {pendingDocs.map(doc => (
              <div key={doc.id} className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">{doc.documentLabel}</span>
                <span className="text-gray-400 text-xs">({doc.fileName})</span>
              </div>
            ))}
          </div>
        )}
      </SummarySection>

      {/* Insurance Summary */}
      <SummarySection title={`Assurances (${insuranceLinks.length})`} icon={Shield}>
        {insuranceLinks.length === 0 ? (
          <p className="text-sm text-gray-500">Aucun contrat d'assurance lie</p>
        ) : (
          <div className="space-y-2">
            {insuranceLinks.map(link => (
              <div key={link.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  {link.termination_confirmed ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-gray-700">{link.contract_name}</span>
                </div>
                <span className={link.termination_confirmed ? 'text-green-600' : 'text-red-600'}>
                  {link.termination_confirmed ? 'Confirme' : 'En attente'}
                </span>
              </div>
            ))}
          </div>
        )}
      </SummarySection>

      {/* Validation Checklist */}
      <SummarySection
        title="Verification avant finalisation"
        icon={AlertTriangle}
        variant={allValid ? 'success' : 'warning'}
      >
        <div className="space-y-1">
          <ValidationItem
            label="Motif de sortie renseigne"
            isValid={validations.reason}
          />
          <ValidationItem
            label="Date effective de sortie"
            isValid={validations.date}
          />
          <ValidationItem
            label="Acte de cession CERFA"
            isValid={validations.cerfa}
            message="Document obligatoire"
          />
          <ValidationItem
            label="Carte grise barree"
            isValid={validations.registration}
            message="Document obligatoire"
          />
          {formData.reason === 'lease_end' && (
            <ValidationItem
              label="PV de restitution leasing"
              isValid={validations.leaseDoc}
              message="Requis pour fin de leasing"
            />
          )}
          <ValidationItem
            label="Resiliations assurance confirmees"
            isValid={validations.insurance}
            message={insuranceLinks.some(l => !l.termination_confirmed) ? 'En attente' : undefined}
          />
        </div>
      </SummarySection>

      {/* Final Warning */}
      {!allValid && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-900">Impossible de finaliser</h4>
              <p className="text-sm text-red-700 mt-1">
                Certaines conditions ne sont pas remplies. Veuillez completer les elements manquants
                avant de finaliser la sortie du vehicule.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Ready to Complete */}
      {allValid && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-green-900">Pret pour finalisation</h4>
              <p className="text-sm text-green-700 mt-1">
                Toutes les conditions sont remplies. Cliquez sur "Finaliser la Sortie" pour
                retirer definitivement ce vehicule de la flotte.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StepExitSummary;
